import React, { RefObject, useEffect, useRef, useState } from 'react';
import vertical from './assets/vertical.svg';
import horizontal from './assets/Horizontals.svg';
import VerticalChapterListing from './VerticalChapterListing';
import remove from '../VideoEditor/assets/delete.svg';
import './Chapters.css'
import { Version } from '../types/presentation';
import { DetailsType } from './ChapterSection';
import { ImageActionState } from './ChapterEditorMain';

interface Chapter {
    id: number;
    title: string;
    image: string;
    duration: number;
    time: number;
}

interface Props {
    chapters: Chapter[];
    // selectedChapters: number[] | null;
    selectedChapters: ImageActionState;
    toggleChapterSelection: (id: number, type: "add" | "remove") => void;
    onSendData: (data: string) => void;
    dataFromChild: string;
    setHasModification: (value: boolean) => void;
    locale: string;
    singleVersion: Version | null;
    details: DetailsType;
    setDetails: React.Dispatch<React.SetStateAction<DetailsType>>;
    seek: (time: number) => void;
}

const Chapters: React.FC<Props> = ({
    chapters,
    selectedChapters,
    toggleChapterSelection,
    onSendData,
    dataFromChild,
    setHasModification,
    details,
    setDetails,
    singleVersion,
    seek,
}) => {
    const [hoveredChapter, setHoveredChapter] = useState<number | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    const HandleInputs = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, id: number) => {
        try {
            setHasModification(true);
            const { name, value } = e.target;
            setDetails(prev => ({
                ...prev,
                [id]: {
                    ...prev[id],
                    [name]: value
                }
            }));
        } catch (error) {
            console.error(error);
        }
    };

    // Re-attach wheel event listener when dataFromChild or details change
    useEffect(() => {
        const el = scrollRef.current;
        if (!el || dataFromChild !== 'horizontal') return;

        const onWheel = (e: WheelEvent) => {
            if (e.deltaY === 0) return;
            e.preventDefault();
            el.scrollLeft += e.deltaY;
        };

        // Clean up previous listener before adding a new one
        el.removeEventListener('wheel', onWheel);
        el.addEventListener('wheel', onWheel, { passive: false });

        return () => {
            el.removeEventListener('wheel', onWheel);
        };
    }, [dataFromChild, details, scrollRef.current]);

    return (
        <div className="position-relative w-auto">
            {/* Button for horizontal and vertical layout for chapters */}
            {chapters && chapters.length > 0 &&
                <div className="d-flex mr-4"
                    style={{
                        position: 'absolute',
                        right: "0",
                        zIndex: 10,
                        transform: "translateY(-40px)",
                        transition: "transform 0.3s ease-in-out",
                    }}
                >
                    <button
                        className="layout-btn left p-2 border-0"
                        onClick={() => dataFromChild === 'horizontal' ? onSendData('') : onSendData('horizontal')}
                    >
                        <img
                            src={horizontal}
                            alt="Horizontal Layout"
                            className={dataFromChild === 'horizontal' ? 'active-icon' : 'inactive-icon'}
                        />
                    </button>

                    <button
                        className="layout-btn right p-2 border-0"
                        onClick={() => dataFromChild === 'vertical' ? onSendData('') : onSendData('vertical')}
                    >
                        <img
                            src={vertical}
                            alt="Vertical Layout"
                            className={dataFromChild === 'vertical' ? 'active-icon' : 'inactive-icon'}
                        />
                    </button>
                </div>
            }

            {
                dataFromChild === 'horizontal' && (
                    <div className="d-flex"
                        ref={scrollRef}
                        style={{
                            overflowX: dataFromChild !== 'horizontal' ? 'hidden' : 'auto',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                        }}
                    >
                        {selectedChapters.chapters.map((chapter, index) => {
                            const chapterTitle =
                                details[chapter.id]?.title !== undefined
                                    ? details[chapter.id].title
                                    : singleVersion?.thumbnails?.[chapter.id]?.title || "";

                            const chapterDescription =
                                details[chapter.id]?.topic !== undefined
                                    ? details[chapter.id].topic
                                    : singleVersion?.thumbnails?.[chapter.id]?.description || "";
                            return (
                                <div
                                    key={index}
                                    className="position-relative flex-grow-1 flex-shrink-0 m-2"
                                    style={{
                                        minWidth: '210px',
                                        maxWidth: '400px',
                                        height: '200px',
                                        cursor: 'pointer',
                                    }}

                                    // onClick={() => toggleChapterSelection(chapter.id, "add")}
                                    onMouseEnter={() => setHoveredChapter(chapter.id)}
                                    onMouseLeave={() => setHoveredChapter(null)}
                                >
                                    <div
                                        className="position-relative hover-container"
                                        style={{ zIndex: 2 }}
                                    >
                                        <label className="font-weight-bold d-block text-white">
                                            Chapter {index + 1}
                                        </label>
                                        <div>
                                            {/* Chapter Title Input */}
                                            <input
                                                type="text"
                                                className="text-white p-2 mb-3"
                                                name="title"
                                                value={chapterTitle || ""}
                                                onChange={(e) => HandleInputs(e, chapter.id)}
                                                style={{
                                                    border: "none",
                                                    width: "100%",
                                                    color: "white",
                                                    opacity: hoveredChapter === chapter.id ? 0.7 : 1,
                                                    transition: "background-color 0.3s ease, opacity 0.3s ease",
                                                    backgroundColor: selectedChapters?.selected.includes(chapter.id) ? "#3f6d8c" : "#666a74",
                                                }}
                                            />
                                            {/* Topic of Chapter Textarea */}
                                            <textarea
                                                className="text-white p-2 mb-2"
                                                name="topic"
                                                value={chapterDescription || ""}
                                                onChange={(e) => HandleInputs(e, chapter.id)}
                                                style={{
                                                    border: "none",
                                                    width: "100%",
                                                    height: "100px",
                                                    color: "white",
                                                    resize: "none",
                                                    overflow: "hidden",
                                                    opacity: hoveredChapter === chapter.id ? 0.7 : 1,
                                                    transition: "background-color 0.3s ease, opacity 0.3s ease",
                                                    backgroundColor: selectedChapters?.selected.includes(chapter.id) ? "#3f6d8c" : "#666a74",
                                                }}
                                            />
                                        </div>

                                        {/* Remove Icon */}
                                        {hoveredChapter === chapter.id && selectedChapters?.selected.includes(chapter.id) && (
                                            <div
                                                className="position-absolute d-flex align-items-center justify-content-center p-4"
                                                style={{
                                                    top: '75px',
                                                    left: '50%',
                                                    transform: 'translateX(-50%) rotate(45deg)',
                                                    backgroundColor: '#32363F',
                                                    color: 'white',
                                                    width: '30px',
                                                    height: '30px',
                                                    borderRadius: '10%',
                                                    cursor: 'pointer',
                                                    zIndex: 10,
                                                }}
                                                onClick={() =>
                                                    toggleChapterSelection(chapter.id, "remove")
                                                }
                                            >
                                                <img src={remove} alt="" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )
            }

            {dataFromChild === 'vertical' && (
                <VerticalChapterListing
                    dataFromChild={dataFromChild}
                    chapters={chapters}
                    selectedChapters={selectedChapters}
                    toggleChapterSelection={toggleChapterSelection}
                    singleVersion={singleVersion}
                    details={details}
                    setDetails={setDetails}
                    setHasModification={setHasModification}
                    seek={seek}
                />
            )}
        </div >
    );
};

export default Chapters;